export type PaymentTraceSource =
	| "ai"
	| "manual"
	| "notification"
	| "screenshot"
	| "csv";

export interface PaymentTraceDraft {
	storeDisplay: string;
	totalAmount: number;
	transactedAt: string;
	source: "notification";
	confidence: "high" | "review";
	warnings: ("merchant" | "date")[];
}

export interface PaymentTraceParseResult {
	records: PaymentTraceDraft[];
	rejected: { index: number; reason: "cancelled" | "amount_missing" }[];
}

const AMOUNT_PATTERN = /(?:₩\s*)?(\d{1,3}(?:,\d{3})+|\d+)\s*원/g;
const DATE_PATTERNS = [
	/(20\d{2})[./-](\d{1,2})[./-](\d{1,2})(?:\s+|\s*T\s*)(\d{1,2}):(\d{2})/,
	/(\d{1,2})[./-](\d{1,2})(?:\s+)(\d{1,2}):(\d{2})/,
] as const;

const NOISE = [
	"승인",
	"결제",
	"체크",
	"신용",
	"일시불",
	"할부",
	"누적",
	"잔액",
	"이용",
	"본인",
	"국내",
	"해외",
	"토스",
	"toss",
	"kb국민카드",
	"국민카드",
	"신한카드",
	"현대카드",
	"삼성카드",
	"롯데카드",
	"우리카드",
	"하나카드",
	"nh농협카드",
	"농협카드",
	"bc카드",
	"카드",
	"카카오페이",
	"네이버페이",
	"payco",
] as const;

function normalizeInput(value: string): string {
	const safe = [...value.normalize("NFKC")]
		.filter((character) => {
			const code = character.charCodeAt(0);
			return !(
				(code >= 0 && code <= 8) ||
				code === 11 ||
				code === 12 ||
				(code >= 14 && code <= 31) ||
				code === 127 ||
				(code >= 0x200b && code <= 0x200d) ||
				code === 0xfeff
			);
		})
		.join("");
	return safe.replace(/\r\n?/g, "\n").trim();
}

function splitNotificationBlocks(value: string): string[] {
	const blankSeparated = value
		.split(/\n\s*\n+/)
		.map((part) => part.trim())
		.filter(Boolean);
	if (blankSeparated.length > 1) return blankSeparated;
	const lines = value
		.split("\n")
		.map((line) => line.trim())
		.filter(Boolean);
	const amountLines = lines.filter((line) => {
		AMOUNT_PATTERN.lastIndex = 0;
		return AMOUNT_PATTERN.test(line);
	});
	return amountLines.length > 1 ? amountLines : value ? [value] : [];
}

function amountFrom(block: string): number | null {
	AMOUNT_PATTERN.lastIndex = 0;
	const values = [...block.matchAll(AMOUNT_PATTERN)]
		.map((match) => Number((match[1] ?? "").replaceAll(",", "")))
		.filter((value) => Number.isSafeInteger(value) && value > 0);
	return values.at(-1) ?? null;
}

function validDate(
	year: number,
	month: number,
	day: number,
	hour: number,
	minute: number,
) {
	return (
		year >= 2000 &&
		year <= 2100 &&
		month >= 1 &&
		month <= 12 &&
		day >= 1 &&
		day <= 31 &&
		hour >= 0 &&
		hour <= 23 &&
		minute >= 0 &&
		minute <= 59
	);
}

function dateFrom(
	block: string,
	now: Date,
): { iso: string; matched: string } | null {
	const full = DATE_PATTERNS[0].exec(block);
	if (full) {
		const [, y = "", m = "", d = "", h = "", min = ""] = full;
		const [year = 0, month = 0, day = 0, hour = 0, minute = 0] = [
			y,
			m,
			d,
			h,
			min,
		].map(Number);
		if (validDate(year, month, day, hour, minute))
			return {
				iso: `${y.padStart(4, "0")}-${m.padStart(2, "0")}-${d.padStart(2, "0")}T${h.padStart(2, "0")}:${min}:00+09:00`,
				matched: full[0],
			};
	}
	const short = DATE_PATTERNS[1].exec(block);
	if (!short) return null;
	const [, m = "", d = "", h = "", min = ""] = short;
	let year = now.getFullYear();
	const tentative = new Date(
		year,
		Number(m) - 1,
		Number(d),
		Number(h),
		Number(min),
	);
	if (tentative.getTime() - now.getTime() > 7 * 86_400_000) year -= 1;
	if (!validDate(year, Number(m), Number(d), Number(h), Number(min)))
		return null;
	return {
		iso: `${year}-${m.padStart(2, "0")}-${d.padStart(2, "0")}T${h.padStart(2, "0")}:${min}:00+09:00`,
		matched: short[0],
	};
}

function cleanMerchantCandidate(value: string): string {
	let cleaned = value
		.replace(/https?:\/\/\S+/gi, " ")
		.replace(/[\w.+-]+@[\w.-]+/g, " ")
		.replace(/\[[^\]]*카드[^\]]*\]/gi, " ")
		.replace(/\b\d{4}[-*•·]\d{2,4}[-*•·]\d{2,4}(?:[-*•·]\d{2,4})?\b/g, " ")
		.replace(/\b\d{8,}\b/g, " ")
		.replace(/\d+\s*개월/g, " ");
	AMOUNT_PATTERN.lastIndex = 0;
	cleaned = cleaned.replace(AMOUNT_PATTERN, " ");
	for (const pattern of DATE_PATTERNS) cleaned = cleaned.replace(pattern, " ");
	for (const word of NOISE)
		cleaned = cleaned.replace(new RegExp(word, "gi"), " ");
	return cleaned
		.replace(/[|:：]/g, " ")
		.replace(/\s+/g, " ")
		.trim()
		.replace(/^[-–—·•]+|[-–—·•]+$/g, "")
		.slice(0, 60);
}

function merchantFrom(block: string, dateMatch: string | null): string | null {
	if (dateMatch) {
		const afterDate = block.slice(block.indexOf(dateMatch) + dateMatch.length);
		const safeAfter = cleanMerchantCandidate(afterDate.split("\n")[0] ?? "");
		if (/[가-힣A-Za-z]/.test(safeAfter) && safeAfter.length >= 2)
			return safeAfter;
	}
	const candidates = block
		.split("\n")
		.map(cleanMerchantCandidate)
		.filter(
			(candidate) => /[가-힣A-Za-z]/.test(candidate) && candidate.length >= 2,
		)
		.filter((candidate) => !/^(알림|문자|push|notification)$/i.test(candidate));
	return candidates.at(-1) ?? null;
}

/**
 * Parses payment notification text in the browser. The return shape deliberately
 * cannot contain raw text, card numbers, approval numbers, or cardholder names.
 */
export function parsePaymentNotificationText(
	input: string,
	now = new Date(),
): PaymentTraceParseResult {
	const normalized = normalizeInput(input);
	const records: PaymentTraceDraft[] = [];
	const rejected: PaymentTraceParseResult["rejected"] = [];
	for (const [index, block] of splitNotificationBlocks(normalized).entries()) {
		if (/(승인\s*취소|결제\s*취소|취소\s*완료|환불|입금)/i.test(block)) {
			rejected.push({ index: index + 1, reason: "cancelled" });
			continue;
		}
		const totalAmount = amountFrom(block);
		if (totalAmount === null) {
			rejected.push({ index: index + 1, reason: "amount_missing" });
			continue;
		}
		const date = dateFrom(block, now);
		const storeDisplay = merchantFrom(block, date?.matched ?? null);
		const warnings: PaymentTraceDraft["warnings"] = [];
		if (!storeDisplay) warnings.push("merchant");
		if (!date) warnings.push("date");
		records.push({
			storeDisplay: storeDisplay ?? "확인 필요",
			totalAmount,
			transactedAt: date?.iso ?? now.toISOString(),
			source: "notification",
			confidence: warnings.length ? "review" : "high",
			warnings,
		});
	}
	return { records, rejected };
}
